import { useEffect, useState } from 'react';
import {
  alpha, Avatar, Box, ButtonBase, Grow, IconButton, Paper, Portal, Stack, Typography
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

export function NotificationToastCard({ item, onOpen, onDismiss }) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hidden, setHidden] = useState(document.visibilityState === 'hidden');
  useEffect(() => {
    const onVisibility = () => setHidden(document.visibilityState === 'hidden');
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);
  useEffect(() => {
    if (hovered || focused || hidden) return undefined;
    const timer = window.setTimeout(() => onDismiss(item.key), 10000);
    return () => window.clearTimeout(timer);
  }, [hovered, focused, hidden, item.key, onDismiss]);
  const initials = item.title.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('');

  return (
    <Grow in timeout={220}>
      <Paper
        elevation={12}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
        }}
        sx={(theme) => ({
          position: 'relative', overflow: 'hidden', borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderLeft: `3px solid ${theme.palette.primary.main}`, pointerEvents: 'auto'
        })}
      >
        <ButtonBase
          onClick={() => onOpen(item.notification)}
          disabled={!item.actionable}
          aria-label={`${item.messageChat ? 'Open chat with' : 'Open notification from'} ${item.title}`}
          sx={(theme) => ({
            p: 2, pr: 5, width: '100%', textAlign: 'left', justifyContent: 'flex-start',
            alignItems: 'flex-start', gap: 1.5,
            '&.Mui-focusVisible': { boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}` },
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) }
          })}
        >
          <Avatar sx={(theme) => ({
            width: 38, height: 38, fontSize: 13, fontWeight: 700,
            color: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.1)
          })}>{initials.toUpperCase()}</Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" color="text.secondary" component="div" noWrap>
              {item.label}{item.count > 1 ? ` · ${item.count} new` : ''}
            </Typography>
            <Typography variant="subtitle2" component="div" noWrap sx={{ fontWeight: 700, my: 0.25 }}>
              {item.title}
            </Typography>
            <Typography variant="body2" component="div"
color="text.secondary" sx={{
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden', overflowWrap: 'anywhere'
            }}>{item.excerpt}</Typography>
            {item.actionable && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {item.messageChat ? 'Open chat' : 'View notification'}
                </Typography>
                <ArrowForwardRoundedIcon sx={{ fontSize: 14 }} />
              </Box>
            )}
          </Box>
        </ButtonBase>
        <IconButton
          size="small"
          aria-label={`Dismiss notification from ${item.title}`}
          onClick={() => onDismiss(item.key)}
          sx={{ position: 'absolute', top: 6, right: 6, width: 32, height: 32 }}
        ><CloseRoundedIcon fontSize="small" /></IconButton>
      </Paper>
    </Grow>
  );
}

export default function NotificationToasts({ items, onOpen, onDismiss }) {
  return (
    <Portal>
      <Stack
        spacing={1.25}
        aria-label="Incoming notifications"
        sx={(theme) => ({
          position: 'fixed', zIndex: theme.zIndex.snackbar, pointerEvents: 'none',
          right: { xs: 12, sm: 24 }, bottom: 'max(16px, env(safe-area-inset-bottom))',
          width: 380, maxWidth: 'calc(100vw - 24px)'
        })}
      >
        {items.slice(0, 3).map((item) => (
          <NotificationToastCard key={item.key} item={item} onOpen={onOpen} onDismiss={onDismiss} />
        ))}
      </Stack>
    </Portal>
  );
}
